import React from "react";
import ParticleField from "../ParticleField";
import { Aurora } from "../Atmosphere";
import { Palette, PALETTES } from "../holidayConfig";
import { range, seeded } from "./sceneUtils";
import { forestPath, forestSnowPath, Grain, H, Layer, Mist, mix, ridge, snowCap, Stars, Tree, W } from "./kit";
import { Gift, GiftSpec, Reindeer, SleighTeam, Snowman } from "./ChristmasCharacters";
import { ToonDefs } from "./toon";
import Enkehus from "./Enkehus";
import { GustafVasaKyrka, skyline, SNOW, SnowyTree, StreetLamp, SubwaySign, Tenement, VasastanDefs } from "./Vasastan";
import "./scenes.css";

/*
 * Jul: julnatt vid Odenplan i Vasastan. Norrsken och måne över stenstadens
 * snötäckta tak, Gustaf Vasa kyrka upplyst inför julottan, Borgerskapets
 * änkehus (kontoret) med adventsljus i fönstren, stadens julgran mitt på
 * torget och tunnelbaneskylten vid uppgången. I förgrunden stora granar
 * med ljus, en snögubbe, en ren, paket och polkagrisar. Julgranskulor
 * hänger ner från skärmens överkant och tomten far förbi i släden: första
 * gången sitter han och vinkar, sedan står han upp och dansar.
 */

const SKY_TOP = "#0b0203";
const SKY_MID = "#1c0406";
const NEEDLE = "#1a0507";
const WARM = "#fff4ec";
/** Färgen som husen blandas mot i julnatten. */
const NIGHT = "#2a0a14";
/** Gatunivå vid Odenplan i mellanlagret. */
const STREET = 488;

/* ------------------------------------------------------------------ */
/* Gemensamma gradienter                                                */
/* ------------------------------------------------------------------ */
const Defs: React.FC<{ p: Palette }> = ({ p }) => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <radialGradient id="xm-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={WARM} stopOpacity="0.9" />
        <stop offset="35%" stopColor={p[200]} stopOpacity="0.35" />
        <stop offset="100%" stopColor={p[300]} stopOpacity="0" />
      </radialGradient>
      <radialGradient id="xm-bulb" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="30%" stopColor={WARM} stopOpacity="0.9" />
        <stop offset="100%" stopColor={p[200]} stopOpacity="0" />
      </radialGradient>
      <linearGradient id="xm-needles" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#2c0a0c" />
        <stop offset="55%" stopColor={NEEDLE} />
        <stop offset="100%" stopColor="#0d0203" />
      </linearGradient>
      <radialGradient id="xm-contact" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={p[900]} stopOpacity="0.55" />
        <stop offset="100%" stopColor={p[900]} stopOpacity="0" />
      </radialGradient>
      <linearGradient id="xm-bank" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff6f6" />
        <stop offset="45%" stopColor={mix("#ffffff", p[200], 0.55)} />
        <stop offset="100%" stopColor={mix(p[300], p[700], 0.3)} />
      </linearGradient>
      <linearGradient id="xm-hill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={mix(p[100], SKY_MID, 0.3)} />
        <stop offset="100%" stopColor={mix(p[400], SKY_MID, 0.62)} />
      </linearGradient>
      <linearGradient id="xm-far" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={mix(p[200], SKY_MID, 0.5)} />
        <stop offset="100%" stopColor={mix(p[500], SKY_MID, 0.72)} />
      </linearGradient>
      <linearGradient id="xm-square" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={mix("#f4e8ec", NIGHT, 0.3)} />
        <stop offset="100%" stopColor={mix(p[300], NIGHT, 0.55)} />
      </linearGradient>
      <filter id="xm-soft" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.4" />
      </filter>
    </defs>
  </svg>
);

/* ------------------------------------------------------------------ */
/* Himlen                                                               */
/* ------------------------------------------------------------------ */
const Moon: React.FC<{ p: Palette }> = ({ p }) => (
  <div className="hol-orb" style={{ left: "79%", top: "19%", width: "clamp(120px, 13vw, 240px)" }}>
    <svg viewBox="-100 -100 200 200">
      <defs>
        <radialGradient id="xm-moon-halo" cx="50%" cy="50%" r="50%">
          <stop offset="20%" stopColor={p[100]} stopOpacity="0.45" />
          <stop offset="45%" stopColor={p[300]} stopOpacity="0.12" />
          <stop offset="100%" stopColor={p[400]} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="xm-moon" cx="40%" cy="36%" r="70%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor={p[50]} />
          <stop offset="100%" stopColor={p[200]} />
        </radialGradient>
      </defs>
      <circle r="100" fill="url(#xm-moon-halo)" />
      <circle r="34" fill="url(#xm-moon)" />
      <g fill={p[200]} opacity="0.55">
        <circle cx="-10" cy="-8" r="7" />
        <circle cx="12" cy="6" r="5" />
        <circle cx="-4" cy="14" r="4" />
        <circle cx="14" cy="-14" r="3" />
      </g>
    </svg>
  </div>
);

/**
 * Tomten i släden. Två flygningar som avlöser varandra: en stor och nära
 * över Odenplan, och en mindre långt bort högt upp som flyger åt andra
 * hållet. Första nära varvet sitter tomten. Sedan dansar han: i bakgrunden
 * den vanliga dansen, och på de nära varven växlar han mellan breakdance
 * (tredje gången han syns) och den vanliga dansen.
 */
const NearSleigh: React.FC<{ p: Palette }> = ({ p }) => (
  <div className="hol-sleigh hol-sleigh-near" style={{ width: "clamp(340px, 42vw, 760px)" }}>
    <div className="hol-flight-inner">
      <SleighTeam p={p} santa="show" />
    </div>
  </div>
);

const FarSleigh: React.FC<{ p: Palette }> = ({ p }) => (
  <div className="hol-sleigh hol-sleigh-far hol-hide-sm" style={{ width: "clamp(190px, 19vw, 330px)" }}>
    <div className="hol-flight-inner" style={{ animationDuration: "5s" }}>
      <SleighTeam p={p} mirrored santa="dance" />
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Granar i förgrunden med ljusslingor                                   */
/* ------------------------------------------------------------------ */
const bulbsFor = (t: Tree, seed: number) => {
  const rnd = seeded(seed);
  const crown = t.h * 0.9;
  const top = t.y - t.h;
  const out: { x: number; y: number; g: number; r: number }[] = [];
  const n = Math.round(t.h / 13);
  for (let k = 2; k < n; k++) {
    const f = k / n;
    const y = top + crown * f;
    const w = t.h * 0.27 * (0.18 + 0.82 * f) * 0.85;
    const side = Math.sin(f * Math.PI * 5.5 + seed);
    out.push({ x: t.x + side * w, y: y + Math.abs(side) * 4, g: Math.floor(rnd() * 3), r: 2 + rnd() * 1.4 });
  }
  return out;
};

const BigPines: React.FC<{ trees: Tree[]; p: Palette }> = ({ trees, p }) => {
  const snow = mix("#ffffff", p[100], 0.4);
  return (
    <g>
      {trees.map((t, i) => (
        <ellipse key={i} cx={t.x + 10} cy={t.y + 2} rx={t.h * 0.3} ry={t.h * 0.04} fill="url(#xm-contact)" />
      ))}
      {trees.map((t, i) => (
        <g key={i}>
          <path d={forestPath([t], 77 + i)} fill="url(#xm-needles)" />
          <path d={forestSnowPath([t], 77 + i)} fill={snow} />
        </g>
      ))}
      {trees.map((t, i) => (
        <g key={i}>
          {bulbsFor(t, i * 13 + 5).map((b, k) => (
            <g key={k} className={`hol-star-group hol-star-group-${b.g}`}>
              <circle cx={b.x} cy={b.y} r={b.r * 2.8} fill="url(#xm-bulb)" />
              <circle cx={b.x} cy={b.y} r={b.r * 0.7} fill="#ffffff" />
            </g>
          ))}
        </g>
      ))}
      {/* Stjärnor i topparna */}
      {trees.filter(t => t.h > 300).map((t, i) => (
        <g key={i} className="hol-breathe">
          <circle cx={t.x} cy={t.y - t.h - 4} r={26} fill="url(#xm-bulb)" />
          <path d={`M${t.x} ${t.y - t.h - 16} L${t.x + 4} ${t.y - t.h - 7} L${t.x + 13} ${t.y - t.h - 5} L${t.x + 6} ${t.y - t.h + 1} L${t.x + 8} ${t.y - t.h + 10} L${t.x} ${t.y - t.h + 5} L${t.x - 8} ${t.y - t.h + 10} L${t.x - 6} ${t.y - t.h + 1} L${t.x - 13} ${t.y - t.h - 5} L${t.x - 4} ${t.y - t.h - 7} Z`} fill="#ffffff" />
        </g>
      ))}
    </g>
  );
};

const CandyCane: React.FC<{ x: number; y: number; rot: number; s?: number; p: Palette; id: string }> = ({ x, y, rot, s = 1, p, id }) => (
  <g transform={`translate(${x} ${y}) rotate(${rot}) scale(${s})`}>
    <defs>
      <clipPath id={id}>
        <path d="M0 0 V -70 C 0 -92, 30 -92, 30 -70" stroke="#000" strokeWidth="9" fill="none" strokeLinecap="round" />
      </clipPath>
    </defs>
    <path d="M0 0 V -70 C 0 -92, 30 -92, 30 -70" stroke="#fffafa" strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M0 0 V -70 C 0 -92, 30 -92, 30 -70" stroke={p[600]} strokeWidth="9" fill="none" strokeDasharray="6 7" />
    <path d="M-2.5 0 V -70 C -2.5 -95, 32.5 -95, 32.5 -70" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="1.6" fill="none" />
    <path d="M3 0 V -70 C 3 -88, 27 -88, 27 -70" stroke={p[950]} strokeOpacity="0.25" strokeWidth="2" fill="none" />
  </g>
);

/* ------------------------------------------------------------------ */
/* Julgranskulor som hänger från överkanten                              */
/* ------------------------------------------------------------------ */
interface BaubleSpec {
  left: string;
  width: string;
  thread: number;
  shades: [string, string, string];
  pattern: "band" | "frost" | "plain";
  duration: number;
  hide?: string;
}

const Bauble: React.FC<{ b: BaubleSpec; id: string; p: Palette }> = ({ b, id, p }) => {
  const s = b.thread;
  const cy = s + 58;
  return (
    <div className={`hol-hang ${b.hide ?? ""}`} style={{ left: b.left, width: b.width, animationDelay: `${300 + parseFloat(b.left) * 12}ms` }}>
      <div className="hol-hang-swing" style={{ animationDuration: `${b.duration}s`, animationDelay: `${-b.duration * 0.4}s` }}>
        <svg viewBox={`-50 0 100 ${s + 110}`}>
          <defs>
            <radialGradient id={`${id}-ball`} cx="36%" cy="30%" r="78%" fx="30%" fy="24%">
              <stop offset="0%" stopColor={b.shades[0]} />
              <stop offset="48%" stopColor={b.shades[1]} />
              <stop offset="100%" stopColor={b.shades[2]} />
            </radialGradient>
            <linearGradient id={`${id}-cap`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8a7373" />
              <stop offset="40%" stopColor="#f8eded" />
              <stop offset="100%" stopColor="#6b5555" />
            </linearGradient>
            <clipPath id={`${id}-clip`}>
              <circle cx="0" cy={cy} r="46" />
            </clipPath>
          </defs>
          <line x1="0" y1="0" x2="0" y2={s + 2} stroke={p[200]} strokeOpacity="0.6" strokeWidth="0.9" />
          <circle cx="0" cy={s + 1} r="3.4" fill="none" stroke="#d9c4c4" strokeWidth="1.3" />
          <circle cx="0" cy={cy} r="46" fill={`url(#${id}-ball)`} />
          <g clipPath={`url(#${id}-clip)`}>
            {b.pattern === "band" && (
              <>
                <path d={`M-50 ${cy - 6} Q 0 ${cy + 8} 50 ${cy - 6} L 50 ${cy + 6} Q 0 ${cy + 20} -50 ${cy + 6} Z`} fill="#ffffff" opacity="0.9" />
                {range(7).map(k => (
                  <circle key={k} cx={-36 + k * 12} cy={cy + 6 + Math.cos(((k - 3) / 4) * 1.2) * 5 - 3} r="2" fill={b.shades[1]} />
                ))}
                <path d={`M-50 ${cy - 16} Q 0 ${cy - 4} 50 ${cy - 16}`} stroke="#ffffff" strokeWidth="1.4" fill="none" opacity="0.8" />
                <path d={`M-50 ${cy + 16} Q 0 ${cy + 30} 50 ${cy + 16}`} stroke="#ffffff" strokeWidth="1.4" fill="none" opacity="0.8" />
              </>
            )}
            {b.pattern === "frost" &&
              range(26).map(k => {
                const a = k * 2.399;
                const rr = Math.sqrt(k / 26) * 42;
                return <circle key={k} cx={Math.cos(a) * rr} cy={cy + Math.sin(a) * rr} r={0.8 + (k % 3) * 0.5} fill="#ffffff" opacity={0.35 + (k % 4) * 0.12} />;
              })}
            {/* Skugga nedtill och reflekterat ljus i kanten */}
            <circle cx="12" cy={cy + 14} r="44" fill={b.shades[2]} opacity="0.25" />
            <path d={`M-40 ${cy + 16} A 44 44 0 0 0 20 ${cy + 42}`} stroke="#ffffff" strokeOpacity="0.35" strokeWidth="2.5" fill="none" />
          </g>
          {/* Fönsterreflex */}
          <path d={`M-30 ${cy - 16} C -28 ${cy - 32}, -14 ${cy - 40}, 0 ${cy - 41} C -10 ${cy - 34}, -20 ${cy - 26}, -24 ${cy - 12} Z`} fill="#ffffff" opacity="0.75" filter="url(#xm-soft)" />
          <circle cx="-26" cy={cy - 4} r="2.6" fill="#ffffff" opacity="0.8" />
          {/* Hätta */}
          <rect x="-10" y={s + 3} width="20" height="12" rx="2" fill={`url(#${id}-cap)`} />
          <path d={`M-10 ${s + 7} H 10 M -10 ${s + 11} H 10`} stroke="#6b5555" strokeWidth="0.6" />
        </svg>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Odenplan                                                             */
/* ------------------------------------------------------------------ */

/** Röda och gyllene kulor på torgets julgran. */
const ornaments = (t: Tree, p: Palette) =>
  range(22).map(i => {
    const f = 0.16 + (i / 22) * 0.78;
    const y = t.y - t.h + t.h * 0.9 * f;
    const w = t.h * 0.27 * (0.18 + 0.82 * f) * 0.78;
    const side = Math.sin(i * 2.39 + 0.6);
    return { x: t.x + side * w, y: y + 6, r: 3.6 + (i % 3) * 0.8, c: i % 3 === 0 ? "#e9c46a" : p[500] };
  });

/** Sankt Matteus kyrkas spetsiga torn långt bort i stenstaden. (x, y) = tornets fot. */
const DistantSpire: React.FC<{ x: number; y: number; fill: string; snow: string }> = ({ x, y, fill, snow }) => (
  <g>
    <rect x={x - 9} y={y - 70} width="18" height="70" fill={fill} />
    <path d={`M${x - 11} ${y - 70} L${x} ${y - 150} L${x + 11} ${y - 70} Z`} fill={fill} />
    <path d={`M${x - 11} ${y - 70} L${x - 5} ${y - 96} L${x - 2} ${y - 80} Z`} fill={snow} opacity="0.7" />
    <path d={`M${x} ${y - 150} V${y - 162} M${x - 4} ${y - 157} H${x + 4}`} stroke={fill} strokeWidth="1.6" />
    <circle cx={x} cy={y - 50} r="4" fill="#ffcf7a" opacity="0.6" />
  </g>
);

/* ------------------------------------------------------------------ */
/* Scenen                                                               */
/* ------------------------------------------------------------------ */
const ChristmasScene: React.FC<{ p: Palette; dark: boolean }> = ({ p, dark }) => {
  const amber = PALETTES.amber;
  const far = skyline(41, -40, W + 40, 430, [1240, 1420]);
  const near = skyline(77, -60, W + 60, 470, [560, 1060]);
  const rNear = ridge({
    seed: 34,
    base: 578,
    amp: 26,
    scale: 2,
    shape: x => 58 * Math.pow(Math.abs(x - 800) / 800, 2.4),
  });

  // Odenplans julgran mitt på torget och de stora granarna i hörnen
  const odenTree: Tree = { x: 800, y: STREET + 8, h: 312 };
  const bigTrees: Tree[] = [
    { x: 26, y: rNear.y(26) + 8, h: 300 },
    { x: 1592, y: rNear.y(1592) + 8, h: 312 },
  ];

  const gifts: GiftSpec[] = [
    { x: 168, y: rNear.y(168) + 30, w: 58, h: 46, d: 32, box: [p[400], p[600], p[800]], ribbon: ["#fff7f7", p[200]] },
    { x: 232, y: rNear.y(232) + 32, w: 40, h: 32, d: 24, box: ["#ffffff", p[50], p[200]], ribbon: [p[500], p[700]] },
    { x: 1430, y: rNear.y(1430) + 34, w: 46, h: 38, d: 26, box: [p[200], p[300], p[500]], ribbon: [p[700], p[900]] },
  ];

  const baubles: BaubleSpec[] = [
    { left: "32.5%", width: "clamp(30px, 3.2vw, 56px)", thread: 34, shades: [p[300], p[600], "#3b0707"], pattern: "band", duration: 5.4, hide: "hol-hide-md" },
    { left: "37%", width: "clamp(24px, 2.4vw, 42px)", thread: 90, shades: ["#ffffff", p[100], p[300]], pattern: "frost", duration: 4.6, hide: "hol-hide-md" },
    { left: "62%", width: "clamp(26px, 2.6vw, 46px)", thread: 70, shades: [p[200], p[400], p[800]], pattern: "plain", duration: 5, hide: "hol-hide-md" },
    { left: "66.5%", width: "clamp(32px, 3.4vw, 60px)", thread: 20, shades: [p[300], p[600], "#3b0707"], pattern: "frost", duration: 6, hide: "hol-hide-md" },
  ];

  const farBody = mix(p[900], SKY_MID, 0.62);
  const nearBody = mix(p[900], SKY_MID, 0.38);
  const treeColor = mix("#120306", SKY_MID, 0.2);

  return (
    <>
      <Defs p={p} />
      <ToonDefs />
      <VasastanDefs />
      <div
        className="hol-sky-fill"
        style={{
          background: `radial-gradient(ellipse 80% 45% at 50% 100%, ${mix(p[700], SKY_MID, 0.35)} 0%, transparent 70%), linear-gradient(180deg, ${SKY_TOP} 0%, ${SKY_MID} 35%, ${mix(p[950], SKY_MID, 0.4)} 65%, ${mix(p[800], SKY_MID, 0.4)} 100%)`,
        }}
      />
      <Aurora p={p} />
      <Stars seed={12} count={260} color="#fff5f5" maxY={440} />
      <div className="hol-shooting-star" style={{ left: "58%", top: "10%" }} />
      <div className="hol-shooting-star" style={{ left: "28%", top: "22%", animationDelay: "-6s", animationDuration: "15s" }} />
      <Moon p={p} />
      <FarSleigh p={p} />

      {/* Stenstadens tak långt bort, med Sankt Matteus torn */}
      <Layer delay={0}>
        <DistantSpire x={520} y={372} fill={farBody} snow={mix(SNOW, SKY_MID, 0.5)} />
        <path d={far.body} fill={farBody} />
        <path d={far.roofs} fill={mix(SNOW, SKY_MID, 0.5)} />
        <path d={far.lit} fill="#ffcf7a" opacity="0.55" />
      </Layer>
      <Mist bottom="24vh" height="16vh" color={`${p[200]}1c`} duration={70} />
      <Layer delay={120}>
        <path d={near.body} fill={nearBody} />
        <path d={near.roofs} fill={mix(SNOW, SKY_MID, 0.3)} />
        <path d={near.lit} fill="#ffcf7a" opacity="0.8" />
      </Layer>

      {/* Odenplan */}
      <Layer delay={240}>
        <path d={`M-40 ${STREET - 2} H${W + 40} V${H} H-40 Z`} fill="url(#xm-square)" />
        <SnowyTree x={600} y={STREET + 4} len={84} width={10} seed={88} color={treeColor} />
        <SnowyTree x={1010} y={STREET + 4} len={80} width={10} seed={93} color={treeColor} />
        <Tenement spec={{ x: -70, w: 186, h: 222, wall: "#d8b067", cols: 5, roof: "turret", seed: 5 }} base={STREET} night={NIGHT} />
        <Tenement spec={{ x: 384, w: 150, h: 214, wall: "#e3d2a0", cols: 4, roof: "gable", seed: 13 }} base={STREET} night={NIGHT} />
        <Enkehus x={250} y={STREET} s={0.85} p={amber} mood="jul" />
        <GustafVasaKyrka x={1330} y={STREET + 2} s={0.9} night={NIGHT} />
        {/* Torgets julgran med kulor och ljus */}
        <ellipse cx={odenTree.x} cy={STREET + 10} rx="200" ry="18" fill="url(#xm-glow)" opacity="0.5" />
        <BigPines trees={[odenTree]} p={p} />
        {ornaments(odenTree, p).map((o, i) => (
          <g key={i}>
            <circle cx={o.x} cy={o.y} r={o.r} fill={o.c} />
            <circle cx={o.x - o.r * 0.35} cy={o.y - o.r * 0.35} r={o.r * 0.32} fill="#ffffff" opacity="0.75" />
          </g>
        ))}
        <SubwaySign x={668} y={STREET + 10} />
        <StreetLamp x={548} y={STREET + 8} />
        <StreetLamp x={1068} y={STREET + 8} delay={-1.8} />
        {/* Snövall längs trottoaren */}
        <path d={snowCap(-40, W + 40, STREET + 8, 5, 61)} fill={mix(SNOW, SKY_MID, 0.12)} />
      </Layer>
      <NearSleigh p={p} />
      <Mist bottom="6vh" height="12vh" color={`${p[100]}1a`} duration={50} reverse />
      <Layer delay={380}>
        <path d={rNear.d} fill="url(#xm-bank)" />
        <BigPines trees={bigTrees} p={p} />
        {gifts.map((g, i) => (
          <Gift key={i} g={g} shadow={p[950]} />
        ))}
        <CandyCane x={318} y={rNear.y(318) + 26} rot={-8} s={0.85} p={p} id="xm-cane-a" />
        <CandyCane x={1262} y={rNear.y(1262) + 26} rot={10} s={0.75} p={p} id="xm-cane-b" />
        <Snowman x={1088} y={rNear.y(1088) + 30} s={0.8} p={p} />
        <Reindeer x={478} y={rNear.y(478) + 30} s={0.95} p={p} />
      </Layer>

      <ParticleField kind="snow" dark={dark} />
      {baubles.map((b, i) => (
        <Bauble key={i} b={b} id={`xm-b${i}`} p={p} />
      ))}
      <Grain opacity={0.06} />
    </>
  );
};

export default ChristmasScene;
